import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../app/store";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import { toast } from "react-toastify";
import FormField from "../../components/Form/FormField";
import FormTextArea from "../../components/Form/FormTextArea";
import { useLocation, useNavigate } from "react-router-dom";
import { createProject, updateProjectById } from "../../app/projectSlice";
import LocationMap from "../../components/Map/MapLoad";
import { DeleteOutlined } from "@ant-design/icons";
import { detailProject } from "../../services/projectServices";
import { Spin } from "antd";
import ErrorBoundary from "../../components/ErrorBoundary";
import GooglePlacesAutocomplete from "../../components/AutoComplete/AutoComplete";

const CreateProject = () => {
  const location = useLocation();
  const { id } = location.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const todayDate = new Date().toISOString().slice(0, 10);
  const initialFormData = {
    name: "",
    status: "active",
    image: null as File | string | null,
    info: "",
    start: "",
    end: "",
    start_latitude: "",
    start_longitude: "",
    end_latitude: "",
    end_longitude: "",
    start_location: "",
    end_location: "",
    vessel_name: "",
    pni: "",
    pni_representative: "",
    vessel_owner: "",
    vessel_owner_representative: "",
    consultant: "",
  };
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formatToDateInput = (datetime: string | Date): string => {
    const date = new Date(datetime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (id) {
        setLoading(true);
        try {
          const response = await detailProject(Number(id));
          const project = response.data;

          setFormData({
            name: project.name || "",
            status: project.status || "active",
            image: project.image || null,
            info: project.brief || "",
            start: project.start ? formatToDateInput(project.start) : "",
            end: project.end ? formatToDateInput(project.end) : "",
            start_latitude: project.start_latitude || "",
            start_longitude: project.start_longitude || "",
            end_latitude: project.end_latitude || "",
            end_longitude: project.end_longitude || "",
            start_location: project.start_location || "",
            end_location: project.end_location || "",
            vessel_name: project.vessel_name || "",
            pni: project.pni || "",
            pni_representative: project.pni_representative || "",
            vessel_owner: project.vessel_owner || "",
            vessel_owner_representative: project.vessel_representative || "",
            consultant: project.consultant || "",
          });
          setPreview(project.image || null);
        } catch (error: any) {
          toast.error(error.message || "Failed to fetch project details");
        } finally {
          setLoading(false);
        }
      } else {
        setFormData(initialFormData);
        setPreview(null);
      }
    };
    fetchProjectDetails();
  }, [id]);
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = "Project Name is required";
    if (!formData.status) newErrors.status = "Status is required";
    if (!formData.start) newErrors.start = "Start date is required";
    if (!formData.start_latitude)
      newErrors.start_latitude = "Casualty Latitude is required";
    if (!formData.start_longitude)
      newErrors.start_longitude = "Casualty Longitude is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleFromSelect = (
    location: { latitude: number; longitude: number },
    address: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      start_latitude: location.latitude.toString(),
      start_longitude: location.longitude.toString(),
      start_location: address,
    }));
    setErrors((prev) => ({
      ...prev,
      start_latitude: "",
      start_longitude: "",
      start_location: "",
    }));
  };
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, files } = e.target as HTMLInputElement;

    if (type === "file") {
      const file = files?.[0] || null;

      if (file) {
        const validTypes = ["image/jpeg", "image/png", "image/jpg"];
        const maxSizeMB = 5;
        const isValidType = validTypes.includes(file.type);
        const isValidSize = file.size <= maxSizeMB * 1024 * 1024;

        if (!isValidType) {
          setErrors((prev) => ({
            ...prev,
            [name]: "Only JPEG, JPG or PNG formats are allowed",
          }));
          setFormData((prev) => ({ ...prev, [name]: null }));
          setPreview(null);
          return;
        }

        if (!isValidSize) {
          setErrors((prev) => ({
            ...prev,
            [name]: "File size should not exceed 5 MB",
          }));
          setFormData((prev) => ({ ...prev, [name]: null }));
          setPreview(null);
          return;
        }
        // If valid, proceed
        setFormData((prev) => ({ ...prev, [name]: file }));
        setPreview(URL.createObjectURL(file));
        setErrors((prev) => ({ ...prev, [name]: "" }));
      } else {
        // If user cancels file selection
        setFormData((prev) => ({ ...prev, [name]: null }));
        setPreview(null);
      }
    } else {
      const noLeadingSpaces = value.replace(/^\s+/, "");
      setFormData((prev) => ({ ...prev, [name]: noLeadingSpaces }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const form = new FormData();
    form.append("name", formData.name);
    form.append("brief", formData.info);
    form.append("status", formData.status);
    form.append("start", formData.start);
    form.append("end", formData.end);
    form.append("start_latitude", formData.start_latitude);
    form.append("start_longitude", formData.start_longitude);
    form.append("end_latitude", formData.end_latitude);
    form.append("end_longitude", formData.end_longitude);
    form.append("start_location", formData.start_location);
    form.append("end_location", formData.end_location);
    form.append("vessel_name", formData.vessel_name);
    form.append("vessel_owner", formData.vessel_owner);
    form.append("vessel_representative", formData.vessel_owner_representative);
    form.append("pni", formData.pni);
    form.append("pni_representative", formData.pni_representative);
    form.append("consultant", formData.consultant);
    if (formData.image instanceof File) {
      form.append("image", formData.image);
    } else {
      form.append("image", "");
    }

    try {
      let result;
      if (id) {
        // Update mode
        result = await dispatch(
          updateProjectById({ id: id, formData: form })
        ).unwrap();
      } else {
        // Create mode
        result = await dispatch(createProject(form)).unwrap();
      }

      toast.success(
        result.message || (id ? "Project updated!" : "Project created!")
      );
      navigate("/dashboard/projectList");
    } catch (error: any) {
      toast.error(error || "Operation failed");
    } finally {
      setLoading(false);
    }
  };
  const handleChanges = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    const trimmedValue = value.replace(/^\s+/, "");

    const isLat = name.includes("latitude");
    const isLng = name.includes("longitude");

    let errorMsg = "";

    // Allow only numbers with optional decimal and minus sign
    const numberRegex = /^-?\d*\.?\d*$/;
    if (!numberRegex.test(trimmedValue)) {
      errorMsg = "Enter a valid number";
    } else {
      const parsed = parseFloat(trimmedValue);
      if (isLat && (parsed < -90 || parsed > 90)) {
        errorMsg = "Latitude must be between -90 and 90";
      } else if (isLng && (parsed < -180 || parsed > 180)) {
        errorMsg = "Longitude must be between -180 and 180";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: errorMsg,
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));
  };

  return (
    <>
      <Breadcrumb pageName={id ? "Update Project" : "Create Project"} />
      {loading ? (
        <div className="flex justify-center items-center h-96">
          <Spin size="large" />
        </div>
      ) : (
        <div className="bg-white dark:bg-boxdark p-6 rounded-md shadow-md max-w-7xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Project Name */}
            <FormField
              label="Project Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={errors.name}
              required
              maxLength={50} // 👈 Set max length here
            />
            {/* Status */}
            <div className="relative">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Status <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className={`appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                    errors.status
                      ? "border-red-500"
                      : "border-stroke dark:border-form-strokedark"
                  }`}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <div className="pointer-events-none absolute top-2/4 right-3 transform -translate-y-1/2 text-gray-400">
                  <svg
                    className="w-4 h-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
              {errors.status && (
                <p className="text-red-500 text-sm mt-1">{errors.status}</p>
              )}
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                Image
              </label>
              <div
                className={`flex items-center w-full rounded-lg border px-4 py-2 bg-gray-100 dark:bg-form-input dark:text-white cursor-pointer ${
                  errors.image
                    ? "border-red-500"
                    : "border-stroke dark:border-form-strokedark"
                }`}
                onClick={() =>
                  document.getElementById("project-image")?.click()
                }
              >
                <input
                  id="project-image"
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleChange}
                  className="hidden"
                />
                <span className="text-sm text-gray-800 dark:text-gray-300">
                  Choose File
                </span>
                <div className="ml-auto flex items-center gap-2 truncate">
                  <span className="text-xs  text-gray-600 dark:text-gray-300 truncate max-w-[150px]">
                    {preview
                      ? formData.image instanceof File
                        ? formData.image.name
                        : preview.split("/").pop()
                      : "No file chosen"}
                  </span>
                  {preview && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData((prev) => ({ ...prev, image: null }));
                        setPreview(null);
                      }}
                      className="text-red-500 hover:text-red-700"
                      title="Remove selected image"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              </div>
              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="mt-2 h-20 w-20 object-cover rounded-full"
                />
              )}
              {errors.image && (
                <p className="text-red-500 text-sm mt-1">{errors.image}</p>
              )}
            </div>
            <FormTextArea
              label="Project Info"
              name="info"
              value={formData.info}
              onChange={handleChange}
              error={errors.info}
            />
            <FormField
              label="Start Date"
              name="start"
              type="date"
              value={formData.start}
              onChange={handleChange}
              required
              error={errors.start}
              max={todayDate}
            />
            <FormField
              type="date"
              name="end"
              label="End Date"
              value={formData.end}
              onChange={handleChange}
              error={errors.end}
              min={formData.start}
            />
            <div className="w-full md:col-span-2">
              <ErrorBoundary>
                <LocationMap
                  onFromLocationSelect={(coords, address) => {
                    setFormData((prev) => ({
                      ...prev,
                      start_latitude: coords.lat.toString(),
                      start_longitude: coords.lng.toString(),
                      start_location: address,
                    }));
                  }}
                  onToLocationSelect={(coords, address) => {
                    if (coords.lat === 0 && coords.lng === 0) {
                      setFormData((prev) => ({
                        ...prev,
                        end_latitude: "",
                        end_longitude: "",
                        end_location: "",
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        end_latitude: coords.lat.toString(),
                        end_longitude: coords.lng.toString(),
                        end_location: address,
                      }));
                    }
                  }}
                  initialFromLocation={
                    formData.start_latitude !== "" &&
                    formData.start_longitude !== "" &&
                    formData.start_latitude !== "0" &&
                    formData.start_longitude !== "0"
                      ? {
                          lat: parseFloat(formData.start_latitude),
                          lng: parseFloat(formData.start_longitude),
                        }
                      : undefined
                  }
                  initialToLocation={
                    formData?.end_latitude &&
                    formData?.end_longitude &&
                    formData.end_latitude !== "" &&
                    formData.end_longitude !== "" &&
                    formData.end_latitude !== "0" &&
                    formData.end_longitude !== "0"
                      ? {
                          lat: parseFloat(formData.end_latitude),
                          lng: parseFloat(formData.end_longitude),
                        }
                      : undefined
                  }
                />
              </ErrorBoundary>
            </div>
            {/* Google Places Autocomplete Section */}
            <div className="mt-6 md:col-span-2">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 text-center">
                Casualty Location Search
              </h2>
              <div className="grid grid-cols-1 gap-6">
                <div className="flex flex-col">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                    Casualty Location
                  </label>
                  <GooglePlacesAutocomplete
                    key={formData.start_location}
                    placeholder="Search Casualty location..."
                    onSelectLocation={handleFromSelect}
                    initialValue={formData.start_location}
                  />
                  {errors.start_location && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.start_location}
                    </p>
                  )}
                </div>

              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block mb-2 text-sm font-semibold text-gray-700 dark:text-white">
                Casualty Location
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  type="number"
                  label="Latitude"
                  name="start_latitude"
                  value={formData.start_latitude}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 20 && /^-?\d*\.?\d*$/.test(value)) {
                      handleChanges(e);
                    }
                  }}
                  error={errors.start_latitude}
                  step="any"
                  min="-90"
                  max="90"
                  required
                  //disabled={true}
                  maxLength={20}
                />
                <FormField
                  type="number"
                  label="Longitude"
                  name="start_longitude"
                  value={formData.start_longitude}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 20 && /^-?\d*\.?\d*$/.test(value)) {
                      handleChanges(e);
                    }
                  }}
                  error={errors.start_longitude}
                  required
                  step="any"
                  min="-180"
                  max="180"
                  //disabled={true}
                  maxLength={20}
                />
              </div>
            </div>
            <FormField
              label="P&I Club"
              name="pni"
              value={formData.pni}
              onChange={handleChange}
              error={errors.pni}
              maxLength={50}
            />
            <FormField
              label="P&I Representative"
              name="pni_representative"
              value={formData.pni_representative}
              onChange={handleChange}
              error={errors.pni_representative}
              maxLength={50}
            />
            <FormField
              label="Vessel Name"
              name="vessel_name"
              value={formData.vessel_name}
              onChange={handleChange}
              error={errors.vessel_name}
              maxLength={50}
            />
            <FormField
              label="Vessel Owner"
              name="vessel_owner"
              value={formData.vessel_owner}
              onChange={handleChange}
              error={errors.vessel_owner}
              maxLength={50}
            />
            <FormField
              label="Owner Representative"
              name="vessel_owner_representative"
              value={formData.vessel_owner_representative}
              onChange={handleChange}
              error={errors.vessel_owner_representative}
              maxLength={50}
            />
            <FormField
              label="Consultant"
              name="consultant"
              value={formData.consultant}
              onChange={handleChange}
              error={errors.consultant}
              maxLength={50}
            />
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-black text-white py-3 rounded-lg transition flex items-center justify-center gap-2 ${
                  loading
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:bg-opacity-90"
                }`}
              >
                {id ? "Update" : "Save"}
                {loading && (
                  <span className="h-4 w-4 ml-2 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default CreateProject;
